<script setup lang="ts">
import { ROUTES } from '@/constants/routes'
import { VIP_MEMBERSHIP_PRICE } from '~/constants/memberships'

const CART_ROUTE = ROUTES.cartRoute.to
</script>

<template>
  <section class="TheTermsOfUse">
    <h2 class="TheTermsOfUse_Heading">Grove Collaborative Terms of Use</h2>
    <p class="TheTermsOfUse_EffectiveDate">Effective: June 14, 2024</p>

    <PoliciesGrovePronounDisclaimer />

    <p>
      Grove Collaborative operates an ecommerce service and related services
      through grove.com, grove.co, mobile applications, and related sites
      accessible to mobile devices, tablets, and other connected devices
      (collectively, the “Grove Collaborative Service” or “Service”). As used in
      this policy, “Grove Collaborative” refers to Grove Collaborative, Inc. and
      its subsidiaries. We may refer to Grove Collaborative as “we”, “us”, or
      “our”. We may refer to you as “consumer”, “you”, or “your”.
    </p>

    <p>
      THE SERVICE IS OFFERED TO YOU SUBJECT TO YOUR ACCEPTANCE OF THESE GROVE
      COLLABORATIVE TERMS OF USE. PLEASE READ THE FOLLOWING GROVE COLLABORATIVE
      TERMS OF USE CAREFULLY. BY CLICKING OR TAPPING “<b>I ACCEPT</b>”, BY “<b
        >CREATING AN ACCOUNT</b
      >”, BY SELECTING “<b>PLACE ORDER,</b>” BY SUBMITTING A SURVEY (INCLUDING
      THE GROVE WELLNESS QUIZ), OR BY ACCESSING OR USING THE SERVICE IN ANY
      MANNER, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE
      BOUND BY THESE GROVE COLLABORATIVE TERMS. IF YOU ARE NOT ELIGIBLE, OR AT
      ANY TIME, DO NOT AGREE TO THE GROVE COLLABORATIVE TERMS, YOU MAY NOT USE
      THE SERVICE.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Privacy Policy</h3>

    <p>
      Please read our
      <SkyLink inline to="/privacy">privacy policy</SkyLink> carefully for
      information relating to our collection, use, storage and disclosure of
      your information. The Grove Collaborative privacy policy is incorporated
      into and is subject to this Grove Collaborative Terms.
    </p>

    <h3 id="additional-terms" class="TheTermsOfUse_Subheading">
      Additional Terms
    </h3>

    <p>
      Your use of the Service is subject to any additional terms, rules, or
      guidelines applicable to the Service or certain features of the Service
      that we may post and you may accept (the “<b>Additional Terms</b>”). All
      such Additional Terms are hereby incorporated by reference into, and made
      a part of, the Grove Collaborative Terms. The Additional Terms include,
      but are not limited to
    </p>

    <ul class="TheTermsOfUse_BulletPoints">
      <li>
        <SkyLink to="/privacy"> Grove Collaborative Privacy Policy </SkyLink>
      </li>
      <li>
        <SkyLink to="/policies/#refund-policy">
          Grove Collaborative Refund Policy
        </SkyLink>
      </li>
      <li>
        <SkyLink to="/policies/#referral-policy">
          Grove Collaborative Referral Program Policy
        </SkyLink>
      </li>
      <li>
        <SkyLink to="/policies/#accessibility-policy">
          Grove Collaborative Accessibility Statement
        </SkyLink>
      </li>
    </ul>

    <p>
      PLEASE BE AWARE THAT THESE GROVE COLLABORATIVE TERMS CONTAIN PROVISIONS
      GOVERNING HOW CLAIMS THAT YOU AND WE HAVE AGAINST EACH OTHER ARE RESOLVED.
      SPECIFICALLY, (1) YOU WILL ONLY BE PERMITTED TO PURSUE CLAIMS AND SEEK
      RELIEF AGAINST US ON AN INDIVIDUAL BASIS, NOT AS A PLAINTIFF OR CLASS
      MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION OR PROCEEDING; AND (2) YOU
      ARE WAIVING YOUR RIGHT TO HAVE A JURY TRIAL ON YOUR CLAIMS.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Grove Collaborative Subscriptions</h3>

    <p>
      The Service is optimally designed for use as a subscription service. As
      part of your first order, you are required to sign up to a product
      subscription program that provides monthly shipments of products in
      previous orders (“<b>Autoship</b>”). You will also receive a trial VIP
      membership with your first order (“<b>VIP Trial</b>”). The VIP Trial will
      automatically renew into an annual paid VIP membership unless otherwise
      stated in your Autoship offer. (VIP membership and Autoship are
      collectively referred to as “<b>Grove Subscriptions</b>”). The Grove
      Subscriptions will automatically renew for additional intervals (Autoship
      will ship monthly and VIP will renew yearly), unless you cancel or modify
      your Grove Subscriptions. After opening your account with Grove
      Collaborative and completing your first order, you may cancel your Grove
      Subscriptions at any time (as described below) and continue to use the
      Service. For additional information on how Grove Subscriptions work read
      the Overview below and visit
      <SkyLink inline to="/how-grove-works" tag="a">How Grove Works</SkyLink>.
    </p>

    <h3 class="TheTermsOfUse_Subheading">
      Grove Collaborative Subscription Overview
    </h3>

    <p>
      <i>Automatic Shipments</i>. Grove Collaborative is primarily a
      subscription service.
    </p>
    <ul class="TheTermsOfUse_BulletPoints">
      <li>
        <strong>How Autoship works</strong>. When you subscribe to specific
        products on the Service for Autoship, you will receive automatic
        shipments of those products, except when (1) your Autoship order is
        under $29; and (2) a product in your Autoship is unavailable (out of
        stock, discontinued, etc.) or otherwise requires a reasonable
        substitution. By subscribing, you understand and agree that we may send
        reasonable substitutions in a variety of circumstances, such as when
        products are unavailable, when your initial order includes a bundled
        consisting in part of certain non-consumable goods (e.g. buckets, spray
        bottles, etc.) that you might not want replenished, when product
        packaging or other features change or updated, or when we send you a
        full size version of products that were initially purchased in a trial
        or sample size. Substitutions may include the same product in a
        different scent or flavor, the same product in a different size, the
        same product in different packaging, only the consumable products of a
        bundle, or a change in your order to move the affected product to a
        different subscription or order (and shipping separately). Your
        subscribed products will be automatically shipped to you on a
        personalized, customizable schedule based on information Grove collects
        from you about your household’s product preferences (in accordance with
        the Grove Collaborative Privacy Policy) and our estimates of the
        quantity of products your household likely needs and how frequently they
        should be replenished. Your first order will include a set of
        recommended and popular items, unless you remove them prior to placing
        the order. After your first order, products automatically sent to you
        will be a personalized assortment from products from your previous
        orders (including free items and recommended items). If you have active
        product subscriptions, these products will be added to your cart in
        timeframes and order size/cost that are based on your previous orders
        with a minimum of $29/order (plus applicable taxes based on your zip
        code). Certain products may be added to your orders as gifts, at no
        charge to you, and will not be included in future shipments. We will
        automatically charge your stored method of payment for your purchase of
        subscribed products that are automatically sent to you under Autoship.
      </li>
      <li>
        <strong>Changing or Canceling Autoship</strong>. You may modify the
        products in and shipment dates of your Autoship, change your product
        subscriptions or cancel Autoship altogether. You will be notified of all
        future orders at the email address you provided and via SMS if you
        provide us with a phone number, so that you may review, change or cancel
        your order before shipment.
        <ul class="TheTermsOfUse_BulletPoints">
          <li>
            To change the date of your next Autoship order or to change the
            frequency of your Autoship, log into your account prior to 9:00 p.m.
            PST on the day before the ship date specified in your Account
            Settings for your then-current order, go to
            <SkyLink inline to="/settings/frequency" tag="a"
              >https://www.grove.co/settings/frequency</SkyLink
            >, and set the desired shipment date or click the desired order
            frequency.
          </li>
          <li>
            To remove or add products to your next Autoship order, log into your
            account prior to 9:00 p.m. PST on the day before the ship date
            specified in your Account Settings for your then-current order, go
            to “Your Cart”, and make changes.
          </li>
          <li>
            To change what products are in your subscription visit your
            <SkyLink inline to="/settings" tag="a"
              >Account Settings page</SkyLink
            >
            and select “Subscriptions”:
            <SkyLink inline to="/settings/subscriptions" tag="a"
              >https://www.grove.co/settings/subscriptions
            </SkyLink>
            to see and update your product subscriptions.
          </li>
        </ul>
      </li>
    </ul>

    <p>
      <i>Buying without Autoship</i>. You do not need a Grove Subscription to
      purchase products from us. If you have no active product subscriptions (or
      otherwise cancel Autoship), you will not receive product shipments
      automatically. You may still purchase products from the Service by
      selecting and ordering desired products.
    </p>

    <p>
      <i>Shipping Details</i>. We use third party carriers to ship items that
      you purchase, and as a result, the risk of loss and title for such items
      pass to you upon our delivery to the carrier.
    </p>

    <p>
      <i>VIP</i>. We offer a VIP program for an annual fee. With your first
      order, you are enrolled in a free trial of Grove's VIP program, which
      gives you free shipping for orders over $29 and occasional free gifts.
    </p>
    <ul class="TheTermsOfUse_BulletPoints">
      <li>
        <strong>Trial Period</strong>. Except when your Autoship program offer
        indicates otherwise, at the end of the specified trial period (usually
        60 days), your free VIP Trial will automatically turn into a recurring,
        annual paid membership for ${{ VIP_MEMBERSHIP_PRICE }} per year, plus
        applicable taxes (based on your zip code) using your stored method of
        payment, unless you cancel before your trial period ends. If you either
        accept an Autoship program offer without a renewing VIP membership or
        choose to cancel your VIP Trial before it becomes a paid VIP membership,
        you will be charged applicable shipping fees with your future orders
        that do not meet the specified minimum (usually, $69).
      </li>
      <li>
        <strong>VIP Automatically renews</strong>. Your paid VIP membership will
        also automatically renew each year and you authorize us to automatically
        charge your stored method of payment a one-year subscription fee (${{
          VIP_MEMBERSHIP_PRICE
        }}
        plus applicable tax, based on your zip code) on an annual basis around
        the anniversary of the date you enrolled in VIP, until you cancel. The
        VIP membership fees are non-refundable, however, if you feel that you
        were charged in error and contact Grove at support@grove.co within 30
        (thirty) days from the date of the charge, Grove may refund the
        subscription fees, at its sole discretion.
      </li>
      <li>
        <strong>Canceling VIP</strong>. You can cancel or manage your VIP Trial
        or VIP membership, and find your annual expiration date from the
        Membership link on the
        <SkyLink inline to="/settings" tag="a">Account Settings page</SkyLink>.
        You may also cancel your VIP Trial or VIP membership by emailing
        <SkyLink inline to="mailto:support@grove.co" tag="a"
          >support@grove.co</SkyLink
        >. If you cancel your VIP subscription after 30 days or more of the
        charge date of your subscription, your VIP Program subscription will
        continue for the remainder of that subscription period and you will not
        receive a refund.
      </li>
    </ul>

    <h3 class="TheTermsOfUse_Subheading">Eligibility</h3>

    <p>
      You must be at least 18 years of age to use the Service. By agreeing to
      these Grove Collaborative Terms or by using the Service, you represent and
      warrant to us: (i) that you are at least 18 years of age; (ii) that you
      have not previously been suspended or removed from the Service; and (iii)
      that your registration and your use of the Service is in compliance with
      any and all applicable laws and regulations. If you are using the Service
      on behalf of an organization, you represent and warrant that you have the
      authority to bind that organization to these Grove Collaborative Terms and
      that you agree to be bound by them on behalf of that organization.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Accounts and Registration</h3>

    <p>
      You need an account to use most features of the Service, and you may be
      required to be logged into your account and have a valid payment method
      associated with it. You may visit your
      <SkyLink inline to="/settings" tag="a">
        Account Settings to manage your payment information.
      </SkyLink>
      You are responsible for maintaining the confidentiality of your account
      and password and for restricting access to your account, and you agree to
      accept responsibility for all activities that occur under your account or
      password. You agree that the information you provide to us is and will be
      accurate, complete and up-to-date at all times. If you provide any
      information that is inaccurate, incomplete or not up-to-date, or if we
      determine in our sole discretion that such information may be inaccurate,
      incomplete or not up-to-date, we have the right to suspend or terminate
      your account and refuse you any and all current or future use of Service.
      You may not register for more than one account or register for an account
      on behalf of an individual other than yourself. If you have reason to
      believe that your account is no longer secure, immediately notify us at
      <SkyLink inline to="mailto:support@grove.co" tag="a"
        >support@grove.co</SkyLink
      >.
    </p>

    <h3 class="TheTermsOfUse_Subheading">
      Payment, Credit Card Billing, Automatic Renewal, and Recurring Charges
    </h3>

    <p>
      <i>Fees to access the service.</i> Access to certain features of the
      Service and certain Grove Subscriptions (such as VIP), may require you to
      pay enrollment or membership fees plus applicable tax. We also charge fees
      for shipping (when not enrolled in VIP or for orders under the stated
      minimum) and supply chain costs. Autoship orders will be charged in
      amounts that may vary based on the type, quantity and frequency of the
      products that you have selected and subscribed to for Autoship.
    </p>

    <p>
      <i>Changes to Fees and Pricing</i>. Grove Collaborative may change any
      fees associated with the Service, including its shipping fee, supply chain
      fee, and the VIP membership fee, on a going forward basis in any manner
      and at any time by providing notice by email or other communications.
      Grove Collaborative also reserves the right to change product pricing in
      any manner and at any time by publishing the new prices on the Service.
      Product pricing changes will be effective when published.
    </p>

    <p>
      <i>Methods of Payment</i>. All amounts charged are in U.S. Dollars. You
      must provide us with a valid credit card (Visa, MasterCard, or any other
      issuer accepted by us) or PayPal account (“Payment Provider”). We will
      charge the Payment Provider you specify in
      <SkyLink inline to="/settings" tag="a">your Account Setting</SkyLink>. By
      providing your Payment Provider information, you authorize us or our third
      party payment processors to charge: (i) all membership or enrollment fees
      to such Payment Provider; and (ii) all fees associated with the Delivery
      Service and Subscription Service, including, without limitation, all
      applicable taxes, product costs and shipping fees, to such Payment
      Provider prior to shipment and on a per-delivery basis. If you pay any
      applicable fees with a credit card, we may seek pre-authorization of your
      credit card account prior to your purchase to verify that the credit card
      is valid and has the necessary funds or credit available to cover your
      purchase. If we do not receive payment from your Payment Provider, you
      must pay all amounts due upon demand and we may suspend your access to the
      Service until such amounts are paid. All sales are final and, except as
      provided in our
      <SkyLink inline to="/policies/#refund-policy">refund policy</SkyLink>, we
      will not issue refunds or accept returns.
    </p>

    <p>
      <i>Minimums.</i> Your order must meet a certain dollar value of purchased
      products to be eligible for free shipping. These minimum amounts vary
      depending on the type of plan and the type of membership you selected. To
      balance our commitment to sustainability while offering the best customer
      experience, we offer the option to ship orders under $29 if you're a VIP,
      and under $69 if you're not, with a $4.99 shipping fee to help offset the
      environmental impact of small orders. We still hope to avoid Grove boxes
      going out with a single item rattling around inside, and we trust our
      customers to make the best decisions for the planet and their families.
      For more information on order minimums, please see our
      <SkyLink
        inline
        to="https://community.grove.co/hc/en-us/articles/115003322963-Is-there-an-order-minimum"
        tag="a"
        >FAQs</SkyLink
      >.
    </p>

    <p>
      <i
        >Authorization for Automatic Renewal of Grove Collaborative
        Subscriptions.</i
      >
      Your Grove Subscriptions will automatically charge as described above
      unless and until you cancel. You hereby authorize us to charge your
      Payment Provider now, and again at the beginning of any subsequent
      subscription period or order. Upon renewal of your subscription, if Grove
      Collaborative does not receive payment from your Payment Provider, (i) you
      agree to pay all amounts due on your account upon demand, and/or (ii) you
      agree that Grove Collaborative may either terminate or suspend the
      Subscription Services and continue to attempt to charge your Payment
      Provider until payment is received (upon receipt of payment, your account
      will be activated and for purposes of automatic renewal, your new
      subscription commitment period will begin as of the day payment was
      received). We will send you reminders of your upcoming subscription
      charges.
    </p>
    <p>
      <i>Referral Program</i>. Subject to the
      <SkyLink inline to="/policies/#referral-policy"
        >Grove Collaborative Referral Program policy </SkyLink
      >, we may offer members the ability to earn credits or discounts
      (“Referral Credits”) toward future shipments of products from Grove
      Collaborative. Please read our
      <SkyLink inline to="/policies/#referral-policy">Referral Program</SkyLink>
      for more details.
    </p>

    <p>
      <i>Third Party Providers</i>. We use and reserve the right to continue
      using third party payment providers (“<b>Payment Processors</b>”). Your
      electronic payment of fees through any Payment Processors is also governed
      by the Payment Processors’ terms of service and privacy policy. Currently,
      we use Stripe, Inc. (“<b>Stripe</b>”) and PayPal, Inc. (“<b>PayPal</b>”)
      as our Payment Processors for payment services (e.g., card acceptance and
      related services), but may add others from time to time. By making
      payments via the Service, you agree to be bound by Stripe’s US Terms of
      Service available at https://stripe.com/us/terms and Privacy Policy
      available at
      <SkyLink inline to="https://stripe.com/us/privacy" tag="a">
        https://stripe.com/us/privacy </SkyLink
      >, PayPal’s User Agreement available at
      <SkyLink
        inline
        to="https://www.paypal.com/us/webapps/mpp/ua/useragreement-full"
        tag="a"
      >
        https://www.paypal.com/us/webapps/mpp/ua/useragreement-full
      </SkyLink>
      and Privacy Policy available at
      <SkyLink
        inline
        to="https://www.paypal.com/us/webapps/mpp/ua/privacy-full"
        tag="a"
      >
        https://www.paypal.com/us/webapps/mpp/ua/privacy-full </SkyLink
      >, as applicable. You hereby consent to provide and authorize Grove
      Collaborative and the Payment Processors to share any information and
      payment instructions you provide to the extent required to complete the
      payment transactions in accordance with the Grove Collaborative Terms,
      including personal, financial, credit card payment, and transaction
      information.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Pre-Sales</h3>

    <p>
      From time to time, we may give you the opportunity to pre-purchase limited
      edition, seasonal or specialty products during a pre-sale event. Your
      account will be charged when you place your pre-sale order. Please allow
      up to one year for your pre-sale items to ship. When the pre-sale products
      arrive in stock, we will send you an email to notify you of the estimated
      delivery date and we will display the pre-sale products in your cart as
      free products. If we’re unable to ship within one year from the date you
      placed your pre-sale order, or if you do not ship your pre-sale products
      within sixty five (65) days of the date you placed your pre-sale order, we
      will cancel the order and provide a full-refund. It is your responsibility
      to keep your billing and shipping information current; we are not
      responsible or liable if you fail to update this information. You can
      update your information at any time prior to your ship date by changing
      the shipping address in your
      <SkyLink inline to="/settings" tag="a">account profile</SkyLink>.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Order Acceptance</h3>

    <p>
      Your receipt of an electronic or other form of order confirmation does not
      signify our acceptance of your order. We reserve the right at any time
      after receipt of your order to accept or decline your order for any
      reason. We further reserve the right at any time after receipt of your
      order, without prior notice to you, to supply less than the quantity you
      ordered of any item. Your order will be deemed accepted by Grove
      Collaborative upon our delivery of the products that you have ordered. We
      may require additional verifications or information before accepting any
      order.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Order Changes and Cancellations</h3>

    <p>
      You may cancel orders provided that we have not begun to process the
      products in your order for shipment. You may do this by
      <SkyLink inline :to="CART_ROUTE" tag="a">visiting your cart</SkyLink>.
    </p>

    <p>
      Your orders and your shipment dates can be modified at any time before the
      ship date specified in your Account Settings for your then-current order.
      You may update the date of your shipment and/or the items in your
      shipment, on
      <SkyLink inline :to="CART_ROUTE" tag="a">grove.co/pantry</SkyLink>. If you
      would like to stop automatic shipments, you can go to
      <SkyLink inline to="/shipments/schedule" tag="a"
        >https://www.grove.co/shipments/schedule</SkyLink
      >
      and click “Pause Auto-Shipments”.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Refunds</h3>

    <p>
      Subject to
      <SkyLink inline to="/policies#refund-policy"
        >the Grove Collaborative Refund Policy</SkyLink
      >, you may request a refund if you are not completely satisfied with your
      purchase. Please read
      <SkyLink inline to="/policies#refund-policy">
        the Grove Collaborative Refund Policy
      </SkyLink>
      for complete details on how to request a refund.
    </p>

    <i>User Generated Content</i>

    <p>
      Our Service provides an opportunity for you to post information in public
      areas of the Service such as your reviews and ratings of specific products
      or the Service (“<b>Customer Reviews</b>”) or other information provided
      to public areas of the Service (collectively, “<b
        >User Generated Content</b
      >”).
    </p>

    <p>
      <i>License Grant to Grove Collaborative and other Customers</i>. You
      retain ownership of copyright and any other proprietary rights you hold in
      User Generated Content that you post to the Service. By posting User
      Generated Content, you grant Grove Collaborative a worldwide,
      non-exclusive, royalty-free, transferable right and license (with the
      right to sublicense) to host, store, transfer, display, perform,
      reproduce, modify, create derivative works of, and distribute your User
      Generated Content, in whole or in part, in any media formats and through
      any media channels (now known or hereafter developed). Any such use of
      your User Generated Content by Grove Collaborative may be without any
      compensation paid to you. By posting and sharing User Generated Content on
      the Service, you hereby grant each user of the Service a worldwide,
      non-exclusive license to access and use your User Generated Content in
      connection with the Service.
    </p>

    <p>
      <i>User Generated Content Representations and Warranties</i>. You are
      solely responsible for your User Generated Content and the consequences of
      posting or publishing User Generated Content. By posting and publishing
      User Generated Content, you affirm, represent, and warrant that:
    </p>

    <ul class="TheTermsOfUse_BulletPoints">
      <li>
        You are the creator and owner of, or have the necessary licenses,
        rights, consents, and permissions to use and to authorize Grove
        Collaborative and users of the Service to use and distribute, your User
        Generated Content as necessary to exercise the licenses granted by you
        in these Grove Collaborative Terms.
      </li>

      <li>
        Your User Generated Content, and the use of it, does not and will not:
        (i) infringe, violate, or misappropriate any third-party right,
        including any copyright, trademark, patent, trade secret, moral right or
        any other intellectual property or proprietary right; or (ii) slander,
        defame, libel, or violate the right of privacy, publicity or other
        property rights of any other person.
      </li>
    </ul>

    <p>
      User Generated Content Disclaimer. Customer Reviews are the author’s
      opinion. They are not our opinion; we have not verified them or approved
      them. We are not obligated to edit, investigate, or control User Generated
      Content that you and other users post or publish, and we will not be in
      any way responsible or liable for User Generated Content. However, we may
      at any time, and without prior notice, remove any User Generated Content
      that in our sole judgment violates these Grove Collaborative Terms or is
      otherwise objectionable. You understand that when using the Service you
      will be exposed to User Generated Content from a variety of sources and
      acknowledge that User Generated Content may be inaccurate, offensive,
      indecent or objectionable. You may request removal of a review that you
      believe violates this Agreement by contacting the Support team at
      <SkyLink inline to="mailto:support@grove.co" tag="a"
        >support@grove.co</SkyLink
      >; but we are not obligated to honor your request.
    </p>

    <h3 class="TheTermsOfUse_Subheading">
      Proprietary Rights of Grove Collaborative
    </h3>

    <p>
      The visual interfaces, graphics, design, compilation, information,
      computer code (including source code or object code), products, software,
      services, and all other elements of the Service provided by Grove
      Collaborative (the “<b>Materials</b>”) are protected by all relevant
      intellectual property and proprietary rights and applicable laws. Except
      for any User Generated Content, all Materials contained in the Service are
      the property of Grove Collaborative or our third-party licensors. Subject
      to these Grove Collaborative Terms, we grant you a limited license to
      reproduce portions of the Materials for the sole purpose of using the
      Service for your personal or internal business purposes. Except for this
      limited license, nothing herein shall be interpreted to grant you any
      right or license under any intellectual property rights of Grove
      Collaborative or any of its affiliates or partners.
    </p>

    <ul class="TheTermsOfUse_BulletPoints">
      <li>
        Trademarks.
        <SkyLink inline to="/policies#trademarks">
          Click here to see a non-exhaustive list of Grove Collaborative
          trademarks</SkyLink
        >. In addition, graphics, logos, page headers, button icons, scripts,
        and service names included in or made available through our Service are
        trademarks or trade dress of Grove Collaborative in the United States
        and other countries. You may not use our trademarks or trade dress in
        connection with any product or service that is not ours, in any manner
        that is likely to cause confusion among customers or members of the
        public, or in any manner that disparages or discredits Grove
        Collaborative. All other trademarks, registered trademarks, product
        names and company names or logos mentioned on the Service are the
        properties of their respective owners who may or may not be affiliated
        with, connected to, or sponsored by Grove Collaborative, and may not be
        copied, imitated or used, in whole or in part, without the permission of
        the applicable trademark holder.
      </li>

      <li>
        Copyrights. All content included in or made available through the
        Service or the Service or on product labels or packaging, such as text,
        graphics, designs, logos, button icons, images, audio clips, digital
        downloads, data compilations, look and feel of the Service, and software
        (“<b>Service Content</b>”) is the property of Grove Collaborative or its
        content suppliers and is protected by United States and international
        copyright laws. Except for any User Generated Content, all Service
        Content contained in the Service or on the Service is the property of
        Grove Collaborative or our third-party licensors. The compilation of all
        content included in or made available through the Service is our
        exclusive property. Subject to these Grove Collaborative Terms, Grove
        Collaborative grants you a limited license to reproduce portions of the
        Service Content for the sole purpose of using the Service for your
        personal or internal business purposes. Except for this limited license,
        nothing herein shall be interpreted to grant you any right or license
        under any intellectual property rights of Grove Collaborative or any of
        its affiliates or partners.
      </li>
      <li>
        Patents. The listed products and/or the use of the listed products in a
        method may be covered by one or more U.S. and/or foreign patents, as set
        forth below. Other patent applications are pending. This page is
        intended to serve as notice under 35 U.S.C. § 287(a).

        <ul class="TheTermsOfUse_BulletPoints">
          <li>
            Auto-Dosing Laundry Detergent Dispenser: U.S. Patent Nos. 10,444,049
            and 10,942,052
          </li>
          <li>
            Canadian Ind. Design Regn. Nos. 188562, 200006, 200007, 200008,
            200009, and 200010
          </li>
          <li>Chinese Design Patent No. ZL201930337370.8.</li>
        </ul>
      </li>
    </ul>

    <h3 class="TheTermsOfUse_Subheading">Digital Millennium Copyright Act</h3>

    <p>
      It is Grove Collaborative’s policy to terminate membership privileges of
      any user who repeatedly engages in copyright infringement upon prompt
      notification to us by the copyright owner or the copyright owner’s legal
      agent.
    </p>

    <p>
      If you are a copyright owner or an agent thereof, and believe that any
      content on the Service infringes your copyrights, you may contact our
      Designated Agent at the following address:
    </p>

    <p>
      Grove Collaborative <br />
      Attn: Designated Agent <br />
      1301 Sansome Street <br />
      San Francisco, CA 94111 <br />
      E-mail:
      <SkyLink inline to="mailto:legal@grove.co">legal@grove.co</SkyLink>
    </p>

    <p>Your notice must include</p>
    <ul class="TheTermsOfUse_BulletPoints">
      <li>your physical or electronic signature;</li>
      <li>
        identification of the copyrighted work claimed to have been infringed,
        or, if multiple copyrighted works at a single online site are covered by
        a single notification, a representative list of such works at that site;
      </li>
      <li>
        identification of the material that is claimed to be infringing or to be
        the subject of infringing activity and information reasonably sufficient
        to permit us to locate the material;
      </li>
      <li>
        a statement that the complaining party has a good-faith belief that use
        of the material in the manner complained of is not authorized by the
        copyright owner, its agent, or the law; and
      </li>
      <li>
        a statement that the complaining party has a good-faith belief that use
        of the material in the manner complained of is not authorized by the
        copyright owner, its agent, or the law; and
      </li>
      <li>
        a statement that the information in the notification is accurate, and
        under penalty of perjury, that the complaining party is authorized to
        act on behalf of the owner of an exclusive right that is allegedly
        infringed
      </li>
    </ul>

    <p>
      If the content you submitted to the Service is removed and you wish to
      contest such removal, you may contact our Designated Agent (indicated
      above). Your written notice must include
    </p>

    <ul class="TheTermsOfUse_BulletPoints">
      <li>your physical or electronic signature</li>
      <li>
        identification of the material that has been removed or to which access
        has been disabled and the location at which the material appeared before
        it was removed or access to it was disabled
      </li>
      <li>
        a statement under penalty of perjury that you have a good faith belief
        that the material was removed or disabled as a result of mistake or
        misidentification of the material to be removed or disabled
      </li>
      <li>
        your name, address, and telephone number, and a statement that you
        consent to the jurisdiction of Federal District Court for the judicial
        district in which your address is located, or if your address is outside
        of the United States, for any judicial district in which Grove
        Collaborative may be found, and that you will accept service of process
        from the person who provided notification.
      </li>
    </ul>

    <h3 class="TheTermsOfUse_Subheading">Prohibited Conduct</h3>

    <p>You will not engage in the following behaviors.</p>
    <ul class="TheTermsOfUse_BulletPoints">
      <li>
        Use the Service for any illegal purpose, or in violation of any local,
        state, national, or international law
      </li>
      <li>
        Violate, or encourage others to violate, the rights of third parties,
        including by infringing or misappropriating third party intellectual
        property rights
      </li>
      <li>
        Use the Service for any commercial purpose, or for any purpose that is
        fraudulent or otherwise tortious or unlawful.
      </li>
      <li>
        Post, upload, or distribute any content that is unlawful, defamatory,
        libelous, inaccurate or that a reasonable person could deem to be
        objectionable, profane, pornographic, harassing, threatening,
        embarrassing, hateful, or otherwise inappropriate
      </li>
      <li>
        Interfere with security-related features of the Service, including by:
        (i) disabling or circumventing features that prevent or limit use or
        copying of any content; or (ii) reverse engineering or otherwise
        attempting to discover the source code of the Service or any part
        thereof except to the extent that such activity is expressly permitted
        by applicable law
      </li>
      <li>
        Make available any virus, adware, spyware, worm, Trojan horse, Easter
        egg, time bomb, spyware, malicious code, or other computer code, file or
        program that is or is potentially harmful or invasive or intended to
        damage or hijack the operation of, or to monitor the use of, any
        hardware, software, or equipment
      </li>
      <li>
        Make unsolicited offers or advertisements to other users of the Service
      </li>
      <li>
        Attempt to collect, personal information about users or third parties
        without their consent
      </li>
      <li>
        Perform any fraudulent activity including impersonating any person or
        entity (including Grove Collaborative or any of our brands), claiming
        false affiliations (including affiliations with Grove Collaborative or
        any of our partners), accessing the Service accounts of others without
        permission, or falsifying your age or date of birth
      </li>
      <li>
        Sell or otherwise transfer your account or content associated with your
        account
      </li>
      <li>
        Use the Service or any of Grove Collaborative’s names, logos, or brands
        without our written consent, including, but not limited to (i) send any
        unsolicited or unauthorized content, including advertising, promotional
        materials, email, junk mail, spam, or other form of solicitation; or
        (ii) use any meta tags or other hidden text or metadata utilizing a
        Grove Collaborative trademark, logo, URL, or product name without our
        written consent
      </li>
      <li>
        Use any robot, spider, site search/retrieval application or other manual
        or automatic device to retrieve, index, “scrape,” “data mine” or
        otherwise gather any material, or reproduce or circumvent the
        navigational structure or presentation of the Service, without our
        express prior written consent
      </li>
      <li>
        Frame or mirror any portion of the Service, or otherwise incorporate any
        portion of the Service into any product or service, unless you obtain
        our express prior written consent to do so
      </li>
      <li>
        Attempt to do anything, or permit, encourage, assist, or allow any third
        party to do anything, prohibited in these Grove Collaborative Terms, or
        attempt, permit, encourage, assist, or allow any other violation of
        these Grove Collaborative Terms of Use, our Refund Policy, our Referral
        Policy, or other applicable policies.
      </li>
    </ul>

    <h3 class="TheTermsOfUse_Subheading">
      Third Party Services and Linked Websites
    </h3>

    <p>
      We may, now or in the future, provide tools through the Service that
      enable you to export information from the Service to third party services,
      including through features that allow you to link your account on Grove
      Collaborative with an account on the third party service, such as Twitter
      or Facebook or through our implementation of third party buttons (such as
      “like” or “share” buttons). By using these tools, you agree that we may
      transfer such information to the applicable third party service. Such
      third party services are not under our control, and we are not responsible
      for their use of your exported information. The Service may also contain
      links to third-party websites. Such linked websites are not under our
      control, and we are not responsible for their content.
    </p>

    <h3 class="TheTermsOfUse_Subheading">
      Termination of Use; Discontinuation and Modification of the Service
    </h3>

    <p>
      These Grove Collaborative Terms are effective on the date when you accept
      them (as described in the preamble above) and remain in full force and
      effect while you use any portion of the Service and for so long as you
      have an account on the Service, unless terminated earlier in accordance
      with these Grove Collaborative Terms. If you violate these Grove
      Collaborative Terms, your permission to use the Service will automatically
      terminate. Additionally, we, in our sole discretion, may terminate your
      account on the Service or suspend or terminate your access to the Service
      at any time, with or without notice. If we terminate your access to the
      Service, we may, in our sole discretion, either fulfill any orders for
      which you have already paid or refund you the relevant amounts paid. We
      also reserve the right to modify the Service at any time without notice to
      you. We will have no liability whatsoever on account of any change to the
      Service and other than as specified in this paragraph, we will have no
      liability as a result of any suspension or termination of your access to
      or use of the Service. You may terminate your account at any time by
      contacting customer service at
      <SkyLink inline to="mailto:support@grove.co" tag="a"
        >support@grove.co</SkyLink
      >. If you terminate your account, you will remain obligated to pay all
      outstanding fees, if any, relating to your use of the Service incurred
      prior to termination.
    </p>

    <h3 class="TheTermsOfUse_Subheading">
      Electronic Communications; SMS and Text Messaging
    </h3>
    <p>
      <i>How We Communicate With You.</i> The Service allows you to receive
      electronic communications (including emails and SMS/MMS mobile messages)
      intended to provide you with information about the Service, upcoming
      orders, offers, products and services. We will communicate with you
      electronically via email, SMS messaging or through the Service when you
      access or use the Service, send us emails or SMS messages, or when we post
      notices on the Service or otherwise communicate with you via email or
      text.
    </p>

    <p>
      <i>Consents for Electronic Communications.</i> You (1) consent to receive
      communications from Grove Collaborative electronically; (2) agree that
      these communications will be subject to these Grove Collaborative Terms of
      Use, including our Privacy Policy; and (3) accept that all terms and
      conditions, agreements, notices, disclosures, and other communications
      that Grove Collaborative provides to you electronically satisfy applicable
      legal requirements for written communications. The foregoing does not
      affect your statutory rights.
    </p>

    <p>
      <i>Text Messaging.</i> Depending on the consent provided, Grove may send
      automated and non-automated text messages that provide you with (1)
      information you have requested; (2) updates regarding your transactions
      with us; (3) updates regarding items in your cart; and/or (4) offers,
      marketing or promotional information about Grove, Grove products and Grove
      services (collectively, “Grove Texts”). Information you provide in Grove
      Texts will be handled as described in our
      <SkyLink inline to="/privacy">Privacy Policy</SkyLink>
    </p>

    <ul class="TheTermsOfUse_BulletPoints">
      <li>
        When you sign up for our Service and provide your mobile number, you
        expressly provide prior written consent to receive Grove Texts to remind
        you about upcoming orders and for other customer support, account or
        Service-related purposes at the mobile number provided.
      </li>
      <li>
        If you enroll in a Grove Text messaging program for marketing or
        promotional information, you will be asked to consent expressly by
        providing us with your mobile number and/or confirming your election to
        opt-in via text and this will constitute your signature to agree to
        receive recurring marketing Grove Texts, including messages sent through
        an automatic telephone dialing system, where such messages may be sent
        by us or our vendors to the mobile number you provided at opt-in. Your
        consent does not suggest or imply that any text messages are in fact
        sent using an automatic telephone dialing system. Your consent is not a
        condition of making any purchase.
      </li>
      <li>
        Message frequency will vary. The number of Grove Texts that you receive
        will vary depending on a number of factors, including how you use the
        Service, your order frequency and applicable Grove programs.
      </li>
      <li>
        <span><strong>Message and data rates may apply.</strong></span> We do
        not charge a fee for Grove Text messaging programs and assume no
        responsibility for charges by your carrier that you may incur when you
        sign up to receive text messages from us.
      </li>
      <li>
        No liability for delayed or undelivered messages. Messages may not be
        delivered to you due to a variety of circumstances or conditions.
        Neither Grove nor any carrier is responsible for any delayed or
        undelivered messages.
      </li>
      <li>
        You may opt out of a Grove Text messaging program at any time by texting
        STOP to the number from which the text messages are being sent. You will
        receive a final confirmation message to confirm that you will no longer
        receive text messages unless you opt back in. You acknowledge that our
        program platforms may not recognize and respond to unsubscribe requests
        that do not include the STOP keyword command and agree that Grove and
        its service providers will have no liability for failing to honor such
        requests.
      </li>
      <li>
        To request more information, text HELP to the number from which text
        messages are being sent. You may also receive help by contacting us at:
        <SkyLink inline to="mailto:support@grove.co" tag="a"
          >support@grove.co</SkyLink
        >.
      </li>
      <li>
        Please notify us immediately if you change your mobile phone number to
        avoid sending notifications to the wrong number. To notify us of any
        change contact Grove at
        <SkyLink inline to="mailto:support@grove.co" tag="a"
          >support@grove.co</SkyLink
        >.
      </li>
    </ul>

    <h3 class="TheTermsOfUse_Subheading">
      Changes to these Grove Collaborative Terms
    </h3>
    <p>
      We reserve the right, at our discretion, to change the Grove Collaborative
      Terms on a going forward basis at any time. When changes are made, we will
      make a new copy of these Terms available on the Service and will update
      the “Last Updated” date at the top of these Terms. In the event that a
      change to these Terms materially modifies your rights or obligations and
      you have registered for an account with us, we will notify you via email
      of the new Grove Collaborative Terms. Any changes to these Terms will be
      effective immediately for new users and will be effective thirty (30) days
      after posting notice of such changes on the Service for existing users,
      provided that any material modifications will be effective for users who
      have an account with us upon the earlier of thirty (30) days after posting
      notice of such changes on the Service or thirty (30) days after dispatch
      of an email notice of such changes. We may require you to provide consent
      to the updated Grove Collaborative Terms in a specified manner before
      further use of the Service or Service is permitted. If you do not agree to
      any change(s) after receiving a notice of such change(s), you will stop
      using the Service and Service. OTHERWISE, YOUR CONTINUED USE OF THE SITE
      OR SERVICE CONSTITUTES YOUR ACCEPTANCE OF SUCH CHANGE(S). Please check
      these Terms regularly for changes.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Indemnity</h3>

    <p>
      You agree that you will be personally responsible for your use of the
      Service, and you agree to defend, indemnify and hold harmless Grove
      Collaborative and its officers, directors, employees, consultants,
      partners, licensors, affiliates, subsidiaries and agents (collectively,
      the “<b>Grove Collaborative Entities</b>”) from and against any and all
      claims, liabilities, damages, losses and expenses, including reasonable
      attorneys’ and accounting fees and costs, arising out of or in any way
      connected with: (i) your misuse of the Service; (ii) your violation of the
      Grove Collaborative Terms or any representation, warranty, or agreements
      referenced herein, or any applicable law or regulation; (iii) your
      violation of any third party right, including without limitation any
      intellectual property right, publicity, confidentiality, property or
      privacy right; or (iv) any disputes or issues between you and any third
      party. We reserve the right, at our own expense, to assume the exclusive
      defense and control of any matter otherwise subject to indemnification by
      you, and in such case, you agree to cooperate with our defense of such
      claim. You agree that the provisions in this section will survive any
      termination of your account, the Grove Collaborative Terms or your access
      to the Service.
    </p>

    <h3 class="TheTermsOfUse_Subheading">No Medical Advice</h3>

    <p>
      NOTHING STATED OR POSTED ON THE SERVICE OR AVAILABLE THROUGH ANY GROVE
      SITE, COMMUNICATION OR PRODUCT IS INTENDED TO BE, AND MUST NOT BE TAKEN TO
      BE, MEDICAL OR OTHER LICENSED PROFESSIONAL ADVICE. GROVE COMMUNICATIONS,
      GRAPHICS, IMAGES, AND INFORMATION OBTAINED FROM THE SERVICE ARE PROVIDED
      FOR INFORMATIONAL PURPOSES ONLY AND NOT INTENDED TO BE A SUBSTITUTE FOR
      PROFESSIONAL MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT OR OTHER PROFESSIONAL
      SERVICES OR ADVICE. ALWAYS SEEK THE ADVICE OF YOUR PHYSICIAN OR OTHER
      QUALIFIED HEALTH PROVIDER WITH ANY QUESTIONS YOU MAY HAVE REGARDING A
      MEDICAL CONDITION. IF YOU THINK YOU MAY HAVE A MEDICAL EMERGENCY, CALL
      YOUR DOCTOR OR 911 IMMEDIATELY.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Disclaimer of Warranties.</h3>

    <p>
      Grove Collaborative cannot and does not represent or warrant that the
      Service or its server will be error-free, uninterrupted, free from
      unauthorized access (including third-party hackers or denial of service
      attacks), or otherwise meet your requirements.
    </p>

    <p>
      THE SERVICE AND ALL INFORMATION, CONTENT, MATERIALS, PRODUCTS, SERVICES,
      AND USER GENERATED CONTENT INCLUDED ON OR OTHERWISE MADE AVAILABLE TO YOU
      THROUGH THE SERVICE (COLLECTIVELY, THE "SITE CONTENTS") ARE PROVIDED BY
      GROVE COLLABORATIVE ON AN "AS IS," "AS AVAILABLE" BASIS, WITHOUT
      REPRESENTATIONS OR WARRANTIES OF ANY KIND. GROVE COLLABORATIVE MAKES NO
      REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE
      OPERATION OF THE SERVICE, THE ACCURACY OR COMPLETENESS OF THE SITE
      CONTENTS, OR THAT EMAILS SENT FROM GROVE COLLABORATIVE ARE FREE OF MALWARE
      OR OTHER HARMFUL COMPONENTS. YOU EXPRESSLY AGREE THAT YOUR USE OF THE
      SERVICE IS AT YOUR SOLE RISK. TO THE FULL EXTENT PERMITTED BY LAW, GROVE
      COLLABORATIVE DISCLAIMS ANY AND ALL REPRESENTATIONS AND WARRANTIES WITH
      RESPECT TO THE SERVICE AND THE SITE CONTENTS, WHETHER EXPRESS OR IMPLIED,
      INCLUDING, WITHOUT LIMITATION, WARRANTIES OF TITLE, MERCHANTABILITY, AND
      FITNESS FOR A PARTICULAR PURPOSE OR USE.
    </p>
    <p>
      CERTAIN PROVIDERS OF PRODUCTS MAY SEPARATELY PROVIDE LIMITED
      REPRESENTATIONS AND/OR WARRANTIES REGARDING THEIR PRODUCTS. THIS
      DISCLAIMER DOES NOT APPLY TO SUCH PRODUCT WARRANTIES.
    </p>

    <p>
      GROVE COLLABORATIVE DOES NOT REPRESENT OR WARRANT THAT THE NUTRITION,
      INGREDIENT, ALLERGEN, AND OTHER PRODUCT INFORMATION ON OUR SERVICE IS
      ACCURATE OR COMPLETE SINCE THIS INFORMATION IS PROVIDED BY THE PRODUCT
      MANUFACTURERS OR SUPPLIERS AND ON OCCASION MANUFACTURERS MAY MODIFY THEIR
      PRODUCTS AND UPDATE THEIR LABELS. WE RECOMMEND THAT YOU DO NOT RELY SOLELY
      ON THE INFORMATION PRESENTED ON OUR SERVICE AND THAT YOU CONSULT THE
      PRODUCT’S LABEL OR CONTACT THE MANUFACTURER DIRECTLY IF YOU HAVE A
      SPECIFIC DIETARY CONCERN OR QUESTION ABOUT A PRODUCT.
    </p>

    <p>
      SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES, THE
      LIMITATION OR EXCLUSION OF IMPLIED WARRANTIES, OR LIMITATIONS ON HOW LONG
      AN IMPLIED WARRANTY MAY LAST, SO SOME OF THE ABOVE LIMITATIONS MAY NOT
      APPLY IN FULL TO YOU AND YOU MAY HAVE ADDITIONAL RIGHTS.
    </p>

    <p>
      IF FOR ANY REASON YOU ARE NOT SATISFIED WITH A PURCHASE YOU MAKE VIA THE
      SERVICE, YOU MAY REQUEST A REFUND IN ACCORDANCE WITH OUR
      <SkyLink inline to="/policies#refund-policy">REFUND POLICY.</SkyLink>
    </p>

    <h3 class="TheTermsOfUse_Subheading">Limitation of Liability</h3>

    <p>
      IN NO EVENT WILL THE GROVE COLLABORATIVE ENTITIES BE LIABLE TO YOU FOR ANY
      INCIDENTAL, SPECIAL, CONSEQUENTIAL OR INDIRECT DAMAGES ARISING OUT OF OR
      IN CONNECTION WITH YOUR USE OF OR INABILITY TO USE THE SERVICE, WHETHER
      BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STATUTE OR ANY
      OTHER LEGAL THEORY, WHETHER OR NOT THE GROVE COLLABORATIVE ENTITIES HAVE
      BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE. YOU ACKNOWLEDGE AND AGREE
      THAT THE AGGREGATE LIABILITY OF THE GROVE COLLABORATIVE ENTITIES TO YOU
      FOR ANY AND ALL CLAIMS ARISING FROM OR RELATED TO THE USE OF THE SERVICE
      OR ANY OTHER INTERACTIONS WITH GROVE COLLABORATIVE IS LIMITED TO THE
      GREATER OF (i) THE AMOUNTS YOU HAVE PAID TO GROVE COLLABORATIVE FOR ACCESS
      TO AND USE OF THE SERVICE IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM OR
      (ii) $100.
    </p>

    <p>
      APPLICABLE LAW MAY NOT ALLOW THE LIMITATION OF LIABILITY SET FORTH ABOVE,
      SO THIS LIMITATION OF LIABILITY MAY NOT APPLY TO YOU, AND YOU MAY HAVE
      RIGHTS ADDITIONAL TO THOSE CONTAINED HEREIN. THE FOREGOING PARAGRAPH SHALL
      NOT APPLY TO RESIDENTS OF NEW JERSEY. WITH RESPECT TO RESIDENTS OF NEW
      JERSEY, THE GROVE COLLABORATIVE ENTITIES SHALL NOT BE LIABLE FOR ANY
      LOSSES OR DAMAGES ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF OR
      INABILITY TO USE THE SERVICE, OR ANY MATERIALS THEREIN UNLESS SUCH DAMAGES
      OR INJURIES ARE THE RESULT OF GROVE COLLABORATIVE’S NEGLIGENT, FRAUDULENT
      OR RECKLESS ACTS OR INTENTIONAL MISCONDUCT.EACH PROVISION OF THESE THAT
      PROVIDES FOR A LIMITATION OF LIABILITY, DISCLAIMER OF WARRANTIES, OR
      EXCLUSION OF DAMAGES IS TO ALLOCATE THE RISKS UNDER THESE TERMS BETWEEN
      YOU AND GROVE COLLABORATIVE. THIS ALLOCATION IS AN ESSENTIAL ELEMENT OF
      THE BASIS OF THE BARGAIN BETWEEN YOU AND GROVE COLLABORATIVE. THE
      LIMITATIONS IN THIS SECTION WILL APPLY EVEN IF ANY LIMITED REMEDY FAILS OF
      ITS ESSENTIAL PURPOSE(S).
    </p>

    <p>
      IF YOU ARE DISSATISFIED WITH THE PRODUCTS DELIVERED THROUGH THE SERVICE,
      YOUR SOLE RECOURSES ARE OUR REFUND POLICY (DESCRIBED ABOVE), AND ANY
      WARRANTY OFFERED BY THE RELEVANT MANUFACTURER.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Governing Law</h3>

    <p>
      These Grove Collaborative Terms and any action related to them shall be
      governed by the laws of the State of California, consistent with the
      Federal Arbitration act, without regard to conflict of law principles. The
      United Nations Convention on Contracts for the International Sale of Goods
      does not apply to these Grove Collaborative Terms. To the extent that any
      lawsuit or court proceeding is permitted hereunder, you and Grove
      Collaborative agree to submit to the personal and exclusive jurisdiction
      of the state courts and federal courts located within San Francisco,
      California for the purpose of litigating all such disputes.
    </p>

    <h3 class="TheTermsOfUse_Subheading">General</h3>

    <p>
      The Grove Collaborative Terms, together with any other agreements
      expressly incorporated by reference herein, constitute the entire and
      exclusive understanding and agreement between you and Grove Collaborative
      regarding your use of and access to the Service. You may not assign or
      transfer these Terms or your rights hereunder, in whole or in part, by
      operation of law or otherwise, without our prior written consent. We may
      assign these Terms at any time without notice. The failure to require
      performance of any provision shall not affect our right to require
      performance at any time thereafter, nor shall a waiver of any breach or
      default of these Terms constitute a waiver of any subsequent breach or
      default or a waiver of the provision itself. Use of paragraph headers in
      these Terms is for convenience only and shall not have any impact on the
      interpretation of particular provisions. We will not be liable for any
      delay or failure to perform resulting from causes outside its reasonable
      control, including, but not limited to, acts of God, war, terrorism,
      riots, embargos, acts of civil or military authorities, fire, floods,
      accidents, strikes or shortages of transportation facilities, fuel,
      energy, labor or materials. In the event that any part of these Terms is
      held to be invalid or unenforceable, the unenforceable part shall be given
      effect to the greatest extent possible and the remaining parts will remain
      in full force and effect. Upon termination of these Terms, any provision
      which, by its nature or express terms should survive, will survive such
      termination or expiration.
    </p>

    <p><strong>Dispute Resolution and Class Action Waiver</strong></p>
    <p>
      THE PROCEDURES AND OTHER TERMS IN THIS SECTION APPLY TO ANY AND ALL
      CLAIMS, DISAGREEMENTS, DISPUTES OR CONTROVERSIES BETWEEN YOU AND GROVE
      COLLABORATIVE OR ITS OFFICERS, DIRECTORS, EMPLOYEES, REPRESENTATIVES,
      AGENTS, PARENTS, AFFILIATES, SUBSIDIARIES AND/OR RELATED COMPANIES,
      REGARDLESS OF WHEN SUCH CLAIM AROSE (EACH, A “<b>CLAIM</b>”), INCLUDING
      BUT NOT LIMITED TO CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE, TO
      THE USE OR ACCESS TO THE SERVICE, TO OUR ADVERTISING, TO THE PRODUCTS,
      SERVICES, MATERIALS, PROGRAMS OR OTHER FEATURES OFFERED, ADVERTISED,
      MARKETED AND/OR SOLD BY GROVE COLLABORATIVE, AND/OR TO THESE TERMS.
    </p>
    <ul class="TheTermsOfUse_BulletPoints">
      <li>
        <i>Mandatory Informal Pre-Suit Dispute Resolution</i>. We value our
        customers and seek to resolve disputes informally where possible. Before
        filing any lawsuit relating to a dispute, you agree to first send a
        detailed notice (“<b>Notice</b>”) to Grove by email at:
        <SkyLink inline to="mailto:support@grove.co" tag="a"
          >support@grove.co</SkyLink
        >. Your Notice must contain all of the following information: (1) your
        full name; (2) your address; (3) your telephone number; (4) your email
        address; (5) information sufficient for Grove to identify the order
        and/or product at issue (e.g., your order number, order confirmation
        email, etc.); and (6) a detailed description of your Claim, the nature
        and basis of your Claim(s), and the nature and basis of the relief you
        are seeking with a calculation for it. You must personally sign this
        Notice. Grove Collaborative also agrees that if we have a dispute with
        you relating to a Grove order or service, we will first send a detailed
        Notice to your e-mail address on file with us, and this Notice will also
        set forth (1) information sufficient for you to identify the order or
        service at issue and (2) a detailed description of our dispute, the
        nature and basis of our claim(s), and the nature and basis of the relief
        we seek.
      </li>
      <li>
        You and Grove agree to negotiate in good faith, for a period of at least
        60 days from the date of the Notice, in an effort to swiftly resolve any
        Claim without the need for a formal proceeding. Compliance with and
        completion of this mandatory informal dispute resolution process is
        required before filing any lawsuit. The statute of limitations and any
        filing fee deadlines will be tolled while the parties engage in this
        process. A court will have the authority to enjoin the filing of any
        lawsuit concerning a Claim without first providing a Notice or otherwise
        participating in good faith in this informal dispute resolution process.
      </li>
      <li>
        <i>Waiver of Class or Other Non-Individualized Relief</i>. TO THE
        FULLEST EXTENT PERMITTED BY APPLICABLE LAW, YOU AND GROVE COLLABORATIVE
        AGREE THAT ANY AND ALL CLAIMS SHALL BE RESOLVED INDIVIDUALLY, WITHOUT
        RESORT TO ANY FORM OF CLASS ACTION OR REPRESENTATIVE ACTION. ALL CLAIMS
        AND DISPUTES MUST BE FILED IN THE COURTS IN SAN FRANCISCO, CALIFORNIA ON
        AN INDIVIDUAL BASIS AND NOT ON A CLASS OR COLLECTIVE BASIS, ONLY
        INDIVIDUAL RELIEF IS AVAILABLE, AND CLAIMS OF MORE THAN ONE CUSTOMER OR
        USER CANNOT BE REVIEWED OR CONSOLIDATED WITH THOSE OF ANY OTHER CUSTOMER
        OR USER.
      </li>
      <li>
        <i>Waiver of Jury Trial</i>. YOU AND GROVE COLLABORATIVE ALSO HEREBY
        VOLUNTARILY AND KNOWINGLY WAIVE ANY CONSTITUTIONAL AND STATUTORY RIGHTS
        TO HAVE A JURY TRIAL. You and Grove Collaborative are instead electing
        that all Claims shall be resolved by a judge on an individual basis.
      </li>
      <li>
        <i>Severability</i>. Except as provided in these Grove Collaborative
        Terms, if any part or parts of these waivers are found under the law to
        be invalid or unenforceable, then such specific part or parts shall be
        of no force and effect and shall be severed and the remainder of the
        waiver shall continue in full force and effect.
      </li>
      <li>
        <i>Survival of Agreement</i>. This section will survive the termination
        of your relationship with Grove Collaborative.
      </li>
      <li>
        <i>Modification</i>. Notwithstanding any provision in this Agreement to
        the contrary, we agree that if Grove Collaborative makes any future
        material change to section, you may reject that change within thirty
        (30) days of such change becoming effective by writing Grove
        Collaborative at the following address: 1301 Sansome St, San Francisco,
        CA 94111.
      </li>
    </ul>

    <h3 class="TheTermsOfUse_Subheading">Notice</h3>

    <p>
      Where we require that you provide an email address, you are responsible
      for providing us with your most current email address. In the event that
      the last email address you provided to us is not valid, or for any reason
      is not capable of delivering to you any notices required/ permitted by the
      Grove Collaborative Terms, our dispatch of the e-mail containing such
      notice will nonetheless constitute effective notice. You may give notice
      to Grove Collaborative at the following address: 1301 Sansome St, San
      Francisco, CA 94111. Such notice shall be deemed given when received by us
      by letter delivered by nationally recognized overnight delivery service or
      first class postage prepaid mail at the above address.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Consumer Complaints</h3>

    <p>
      In accordance with California Civil Code §1789.3, you may report
      complaints to the Complaint Assistance Unit of the Division of Consumer
      Services of the California Department of Consumer Affairs by contacting
      them in writing at 400 R Street, Sacramento, CA 95814, or by telephone at
      <SkyLink inline to="tel:800-952-5210" tag="a">(800) 952-5210</SkyLink>.
    </p>

    <h3 class="TheTermsOfUse_Subheading">International Users</h3>

    <p>
      The Service is controlled and offered by Grove Collaborative from its
      facilities in San Francisco, California. We make no representations that
      the Service is appropriate or available for use in other locations. Those
      who access or use our Service from other countries do so at their own
      volition and are responsible for compliance with local law.
    </p>

    <h3 class="TheTermsOfUse_Subheading">Accessibility</h3>

    <p>
      <SkyLink inline to="/policies#accessibility-policy">
        Please see our Accessibility Statement.
      </SkyLink>
    </p>

    <p>
      Contact Information. If you have any questions, complaints, or claims
      regarding Grove Collaborative, the Service, or these Grove Collaborative
      Terms of Use please contact us at
      <SkyLink inline to="mailto:legal@grove.co" tag="a">legal@grove.co</SkyLink
      >.
    </p>
  </section>
</template>

<style lang="scss">
.TheTermsOfUse {
  display: grid;
  row-gap: var(--spacing-3x);

  &_Heading {
    @include type-brand-heading('2');
    margin-top: var(--spacing-8x);
  }

  &_Subheading {
    @include type-brand-heading('3');
    margin-top: var(--spacing-6x);
  }

  &_EffectiveDate {
    @include type-body('l', true);
    margin-bottom: var(--spacing-3x);
  }

  &_BulletPoints {
    margin: 0;
    display: grid;
    row-gap: var(--spacing-2x);
  }
}
</style>
